import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, Building2, Search, X } from 'lucide-react';
import { useCompanyLookup, CompanyData } from '@/hooks/useCompanyLookup';
import { cn } from '@/lib/utils';

interface CompanySearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onCompanyFound?: (data: CompanyData) => void;
  onClear?: () => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showCompanyPreview?: boolean;
  autoFillFields?: {
    companyName?: (value: string) => void;
    address?: (value: string) => void;
    postalCode?: (value: string) => void;
    city?: (value: string) => void;
    siret?: (value: string) => void;
    codeNaf?: (value: string) => void;
  };
}

export function CompanySearchInput({
  value,
  onChange,
  onCompanyFound,
  onClear,
  label = "SIREN / SIRET",
  placeholder = "Entrez 9 chiffres (SIREN) ou 14 chiffres (SIRET)",
  required = false,
  disabled = false,
  className,
  showCompanyPreview = true,
  autoFillFields
}: CompanySearchInputProps) {
  const [inputValue, setInputValue] = useState(value);

  const { search, companyData, isLoading, error, isVerified, reset } = useCompanyLookup({
    onCompanyFound: (data) => {
      onCompanyFound?.(data);
      
      // Auto-remplir les champs si configurés
      if (autoFillFields) {
        autoFillFields.companyName?.(data.denomination);
        autoFillFields.address?.(data.adresse);
        autoFillFields.postalCode?.(data.code_postal);
        autoFillFields.city?.(data.ville);
        autoFillFields.siret?.(data.siret);
        autoFillFields.codeNaf?.(data.code_naf);
      }
    }
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 14);
    setInputValue(newValue);
    onChange(newValue);
    
    // Lancer la recherche automatique
    if (newValue.length >= 9) {
      search(newValue);
    } else if (newValue.length < 9 && isVerified) {
      reset();
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    reset();
    onClear?.();
  };

  const getStatusIcon = () => {
    if (isLoading) {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (isVerified && companyData) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    if (error) {
      return <XCircle className="h-4 w-4 text-destructive" />;
    }
    return <Search className="h-4 w-4 text-muted-foreground" />;
  };

  const isValidLength = inputValue.length === 9 || inputValue.length === 14;

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor="company-search" className="flex items-center gap-2">
        <Building2 className="h-4 w-4" />
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      
      <div className="relative">
        <Input
          id="company-search"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pr-20",
            isVerified && "border-green-500 focus-visible:ring-green-500",
            error && "border-destructive"
          )}
          maxLength={14}
        />
        
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          {getStatusIcon()}
        </div>
      </div>

      {/* Indicateur de format */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{inputValue.length}/14 chiffres</span>
        {inputValue.length >= 9 && inputValue.length < 14 && (
          <Badge variant="outline" className="text-xs">SIREN</Badge>
        )}
        {inputValue.length === 14 && (
          <Badge variant="outline" className="text-xs">SIRET</Badge>
        )}
      </div>

      {/* Message d'erreur */}
      {error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Prévisualisation entreprise */}
      {showCompanyPreview && isVerified && companyData && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-green-800 dark:text-green-200 truncate">
                {companyData.denomination}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                {companyData.ville} • {companyData.code_naf}
              </p>
              {companyData.suggested_opco && (
                <Badge variant="secondary" className="mt-1 text-xs">
                  OPCO suggéré: {companyData.suggested_opco}
                </Badge>
              )}
            </div>
            <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-800 border-green-300">
              Vérifié INSEE
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}
